import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, Clock, Info, Smartphone, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { instrumentGroup, topup, useTopUpOptions, type TopUp, type TopUpInstrument } from '@/api/topup'
import { Card } from '@/components/ui/card'
import { Button, LinkButton } from '@/components/ui/button'
import { Field, Input, Select } from '@/components/ui/form'
import { npr, nprWhole } from '@/lib/format'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { cn, errorText } from '@/lib/utils'

const QUICK = [500, 1000, 2000, 5000]
const GROUPS = [
  ['mobile', 'Mobile banking'],
  ['internet', 'Internet banking'],
  ['card', 'Cards'],
  ['wallet', 'Wallets'],
  ['other', 'Other'],
] as const

/** Leave the site for our checkout page, which posts on to the gateway. */
const goToCheckout = (url: string) => window.location.assign(url)

/**
 * Top up the Saadhan wallet online. Hidden (the parent shows the counter
 * note only) until the API has the payment gateway.
 */
export function TopUpCard({ hasSuperCard }: { hasSuperCard: boolean }) {
  const { t } = useI18n()
  const options = useTopUpOptions()
  const [text, setText] = useState('')
  const [bank, setBank] = useState('')

  const amount = Number(text)
  const o = options.data
  const valid = o != null && Number.isInteger(amount) && amount >= o.min_amount && amount <= o.max_amount
  const error =
    text && o && !valid
      ? Number.isInteger(amount)
        ? t('Top up between {min} and {max}.', { min: nprWhole(o.min_amount), max: nprWhole(o.max_amount) })
        : t('Top up in whole rupees.')
      : undefined

  const quote = useQuery({
    queryKey: ['topup-quote', amount, bank],
    queryFn: () => topup.quote(amount, bank),
    enabled: valid && !!bank,
    retry: false,
  })
  const start = useMutation({
    mutationFn: () => topup.start(amount, bank || null),
    onSuccess: (r) => goToCheckout(r.checkout_url),
    onError: (e) => toast.error(errorText(e)),
  })

  if (options.isLoading || options.error || !o) return null

  const groups = GROUPS.map(([key, label]) => [label, o.instruments.filter((i) => instrumentGroup(i.type) === key)] as const).filter(
    ([, list]) => list.length,
  )

  return (
    <Card className="mt-4 p-4">
      <p className="font-semibold">{t('Top up online')}</p>
      <p className="mt-1 text-sm text-text-2">{t('Pay from your bank with Nepal Payment Solutions. It goes to your Saadhan wallet, which your super card charges from.')}</p>
      {!hasSuperCard ? (
        <p className="mt-3 flex gap-2 rounded-lg bg-warning-soft p-3 text-sm">
          <Info className="mt-0.5 size-4 shrink-0" />
          {t("You don't have a super card yet. You need one to charge from this wallet; ask the staff at any station.")}
        </p>
      ) : null}

      <form
        className="mt-4 grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) start.mutate()
        }}
      >
        <Field label={t('Amount (Rs)')} error={error} hint={t('From {min} to {max}', { min: nprWhole(o.min_amount), max: nprWhole(o.max_amount) })}>
          {(p) => <Input {...p} inputMode="numeric" value={text} onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))} placeholder="1000" />}
        </Field>
        <div className="flex flex-wrap gap-2">
          {QUICK.filter((q) => q >= o.min_amount && q <= o.max_amount).map((q) => (
            <Button key={q} size="sm" variant={amount === q ? 'primary' : 'outline'} onClick={() => setText(String(q))}>
              {nprWhole(q)}
            </Button>
          ))}
        </div>
        {o.instruments.length ? (
          <Field label={t('Pay with')}>
            {(p) => (
              <Select {...p} value={bank} onChange={(e) => setBank(e.target.value)}>
                <option value="">{t('Choose on the payment page')}</option>
                {groups.map(([label, list]) => (
                  <optgroup key={label} label={t(label)}>
                    {list.map((i: TopUpInstrument) => (
                      <option key={i.code} value={i.code}>
                        {i.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        <div className="rounded-lg bg-surface-2 p-3 text-sm">
          {valid && bank && quote.data ? (
            <>
              <p className="flex justify-between"><span className="text-text-2">{t('Added to your wallet')}</span><span className="tabular">{npr(quote.data.amount)}</span></p>
              <p className="flex justify-between"><span className="text-text-2">{t('Bank service charge')}</span><span className="tabular">{npr(quote.data.service_charge)}</span></p>
              <p className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>{t('You pay')}</span><span className="tabular">{npr(quote.data.total)}</span></p>
            </>
          ) : (
            <p className="text-text-2">{t('The bank adds a small service charge on top. You see it before you pay.')}</p>
          )}
        </div>

        <Button type="submit" disabled={!valid} loading={start.isPending}>
          {valid ? t('Top up {amount}', { amount: nprWhole(amount) }) : t('Top up')}
        </Button>
      </form>
    </Card>
  )
}

const POLL_MS = 3000
const POLL_FOR = 20

/**
 * Where the gateway sends the driver back (?ref=&status=&amount=&from=).
 * The query only says what to show first: a signed-in driver's top-up is
 * read from the API, which asks the gateway itself, and refreshed until done.
 */
export function TopUpResultPage() {
  const { t } = useI18n()
  const { user } = useAuth()
  const [params] = useSearchParams()
  const ref = params.get('ref')
  const fromApp = params.get('from') === 'app'
  const [polls, setPolls] = useState(0)

  const q = useQuery({
    queryKey: ['topup', ref],
    queryFn: async () => {
      setPolls((n) => n + 1)
      return topup.get(ref!)
    },
    enabled: !!ref && !!user,
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === 'pending' && polls < POLL_FOR ? POLL_MS : false),
  })

  const shown = params.get('status') ?? ''
  const order: Pick<TopUp, 'status' | 'amount'> & Partial<TopUp> = q.data ?? {
    status: (['success', 'failed', 'expired', 'mismatch'].includes(shown) ? shown : 'pending') as TopUp['status'],
    amount: Number(params.get('amount')) || 0,
  }
  if (!ref) {
    return <Result tone="neutral" icon={<Info />} title={t("We couldn't find that payment")} body={t('Check your wallet for the latest balance.')} action={fromApp ? null : <LinkButton to="/wallet">{t('Go to wallet')}</LinkButton>} />
  }

  const back = fromApp ? (
    <p className="flex items-center justify-center gap-2 text-sm text-text-2"><Smartphone className="size-4" />{t('You can close this page and go back to the Saadhan Charge app.')}</p>
  ) : (
    <LinkButton to="/wallet">{t('Go to wallet')}</LinkButton>
  )
  const reference = <p className="mt-3 text-xs text-text-2">{t('Reference {ref}', { ref })}</p>

  if (order.status === 'success') {
    return (
      <Result tone="success" icon={<CheckCircle2 />} title={t('{amount} added to your wallet', { amount: nprWhole(order.amount) })} body={order.total_paid ? t('You paid {total} including the bank service charge.', { total: npr(order.total_paid) }) : null} action={back} footer={reference} />
    )
  }
  if (order.status === 'failed') {
    return (
      <Result tone="danger" icon={<XCircle />} title={t('The payment didn’t go through')} body={t('Nothing was added to your wallet. If money left your bank account, contact us with the reference below.')} action={back} footer={reference} />
    )
  }
  if (order.status === 'expired' || order.status === 'mismatch') {
    return (
      <Result tone="neutral" icon={<Info />} title={t('We couldn’t confirm this payment')} body={t('If money left your bank account, contact us with the reference below and we’ll sort it out.')} action={back} footer={reference} />
    )
  }
  const waiting = !user || polls >= POLL_FOR
  return (
    <Result
      tone="neutral"
      icon={<Clock />}
      title={t('Waiting for your bank to confirm')}
      body={waiting ? t('Your wallet is credited as soon as the bank confirms. This can take a few minutes.') : t('This page updates by itself.')}
      action={back}
      footer={reference}
    />
  )
}

function Result({ tone, icon, title, body, action, footer }: { tone: 'success' | 'danger' | 'neutral'; icon: React.ReactNode; title: string; body?: React.ReactNode; action?: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <Card className="mx-auto mt-6 max-w-md p-6 text-center">
      <div className={cn('mx-auto mb-3 grid size-12 place-items-center rounded-full [&_svg]:size-6', tone === 'success' ? 'bg-success-soft text-success' : tone === 'danger' ? 'bg-danger-soft text-danger' : 'bg-surface-3 text-text-2')}>{icon}</div>
      <h1 className="text-lg font-semibold">{title}</h1>
      {body ? <p className="mt-2 text-sm text-text-2">{body}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
      {footer}
    </Card>
  )
}
